import { useTrans } from "../hooks";
import { Box, Text } from "@chakra-ui/react";
import { AppSelect, AppSelectOption } from "./AppSelect";
import { useRecoilState, useRecoilValue } from "recoil";
import {
    ActiveSchoolOrCompanyAtom,
    UserCompanyListAtom,
    UserSchoolListAtom,
} from "../atoms";
import { CompanyApi, SchoolApi } from "../apis";
import { cPath, LSC } from "../config";

export const AppSchoolCompanySelector = () => {
    const { t } = useTrans();
    const schoolData = useRecoilValue(UserSchoolListAtom);
    const schoolOptions = schoolData?.map((e) => {
        return {
            value: SchoolApi.toResourceIRI(e.id),
            label: e.name,
        };
    });
    const companyData = useRecoilValue(UserCompanyListAtom);
    const companyOptions = companyData?.map((e) => {
        return {
            value: CompanyApi.toResourceIRI(e.id),
            label: e.name,
        };
    });
    const [activeSchoolOrCompany, setActiveSchoolOrCompany] = useRecoilState(
        ActiveSchoolOrCompanyAtom
    );

    const groupedOptions: { label: string; options: AppSelectOption[] }[] = [];
    if (schoolOptions) {
        groupedOptions.push({
            label: t("com.AppSchoolCompanySelector:text.schools"),
            options: [...schoolOptions],
        });
    }
    if (companyOptions) {
        groupedOptions.push({
            label: t("com.AppSchoolCompanySelector:text.companies"),
            options: [...companyOptions],
        });
    }

    const onChange = (value: string) => {
        localStorage.setItem(LSC.APP_SEL_SC, `${value}`);
        const sel = [...schoolData, ...companyData]?.filter((d) =>
            d?.id === activeSchoolOrCompany?.id ? d : false
        );
        if (sel) {
            setActiveSchoolOrCompany(sel[0]);
            window.location.href = cPath("ADMIN.DASHBOARD_PAGE");
        }
    };

    const controlLabel: string[] = [];
    if (schoolOptions?.length > 0) {
        controlLabel.push(t("com.AppSchoolCompanySelector:text.schools"));
    }
    if (companyOptions?.length > 0) {
        controlLabel.push(t("com.AppSchoolCompanySelector:text.companies"));
    }

    const isSchoolSelected = schoolOptions?.some(
        (d) => d.value === activeSchoolOrCompany?.["@id"]
    );
    const isCompanySelected = companyOptions?.some(
        (d) => d.value === activeSchoolOrCompany?.["@id"]
    );

    return (
        <Box width={{ base: "100%", md: "302px" }} minWidth="140px">
            <AppSelect<AppSelectOption>
                options={groupedOptions}
                onChange={(e) => onChange(`${e?.value}`)}
                value={[...schoolOptions, ...companyOptions]?.filter(
                    (d) => d?.value === activeSchoolOrCompany?.["@id"]
                )}
                placeholder={`${t(
                    "com.AppSchoolCompanySelector:placeholder.select"
                )} ${controlLabel.join(" / ")}`}
                isClearable={false}
                styles={{
                    container: (base) => ({
                        ...base,
                        width: "100%",
                        minWidth: "140px",
                    }),
                    control: (baseStyles, state) => ({
                        ...baseStyles,
                        height: "50px",
                        borderColor: state.isFocused
                            ? "var(--chakra-colors-primary)"
                            : "var(--chakra-colors-greyT65)",
                        ":hover": {
                            borderColor: state.isFocused
                                ? "var(--chakra-colors-primary)"
                                : "var(--chakra-colors-greyT40)",
                        },
                    }),
                }}
            />
            <Text
                fontSize="0.5rem"
                position="absolute"
                top="0.3125rem"
                bg="var(--chakra-colors-white)"
                px={2}
                py={0}
                ml={2}
                textTransform="uppercase"
            >
                {schoolOptions?.length > 0 && (
                    <Text
                        as="span"
                        fontFamily={
                            isSchoolSelected ? "Inter Bold" : "Inter Regular"
                        }
                        color={
                            isSchoolSelected
                                ? "var(--chakra-colors-primary)"
                                : "inherit"
                        }
                    >
                        {t("com.AppSchoolCompanySelector:text.schools")}
                    </Text>
                )}
                {schoolOptions?.length > 0 &&
                    companyOptions?.length > 0 &&
                    " / "}
                {companyOptions?.length > 0 && (
                    <Text
                        as="span"
                        fontFamily={
                            isCompanySelected ? "Inter Bold" : "Inter Regular"
                        }
                        color={
                            isCompanySelected
                                ? "var(--chakra-colors-primary)"
                                : "inherit"
                        }
                    >
                        {t("com.AppSchoolCompanySelector:text.companies")}
                    </Text>
                )}
            </Text>
        </Box>
    );
};
